import { useMatch, useNavigate } from "react-router-dom";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

import { useTranslation } from "common/locales";
import { EditorSelectionType } from "features/mapDrawTools/enums";
import { useChangeFieldVisibilityQuery } from "model";
import { useAppStore, useUIStore } from "stores";

interface ConfirmCancellationDialogProps {
  open: boolean;
  onClose: (value?: string) => void;
}

export default function ConfirmCancellationDialog(
  props: ConfirmCancellationDialogProps
) {
  const { onClose, open } = props;
  const { t } = useTranslation();
  const fieldEditMatch = useMatch("/edit-field/:fieldId/*");
  const fieldAddMatch = useMatch("/add-field/*");
  const animalGroupFeedAllocationMatch = useMatch(
    "/animal-group/:animalGroupId/feed-allocation"
  );
  const animalGroupAddMatch = useMatch("/add-animal-group");
  const addCoversMatch = useMatch("/add-covers/*");

  // Use Zustand for current farm ID
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const navigate = useNavigate();
  const changeFieldVisibility = useChangeFieldVisibilityQuery();
  const isEditing = useUIStore((state) => state.isEditing);
  const {
    setEditorSelectionType,
    setEditorIsInTentativeMode,
    setIsEditing,
    deleteTmpFeatures,
  } = useUIStore.getState();

  const handleCancel = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (fieldEditMatch || fieldAddMatch) {
      setEditorSelectionType(EditorSelectionType.NONE); // reset editor
      setEditorIsInTentativeMode(false);
      if (isEditing) {
        setIsEditing(false);
      }
      if (fieldEditMatch) {
        changeFieldVisibility({
          guid: fieldEditMatch.params.fieldId ?? "",
          visibility: "visible",
        });
      }
      deleteTmpFeatures();
      if (fieldEditMatch) {
        navigate(`/field/${fieldEditMatch.params.fieldId}`);
      }
      if (fieldAddMatch) {
        navigate(`/farm/${currentFarmId || ""}`);
      }
    }

    if (animalGroupAddMatch) {
      navigate(`/farm/${currentFarmId || ""}`);
    }

    if (animalGroupFeedAllocationMatch) {
      navigate(
        `/animal-group/${animalGroupFeedAllocationMatch.params.animalGroupId}`
      );
    }

    if (addCoversMatch) {
      navigate(`/farm/${currentFarmId || ""}`);
    }

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "24px",
            padding: "16px",
            maxWidth: "320px",
            margin: "16px",
          },
        },
      }}
    >
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          fontSize: "1.25rem",
          fontWeight: 600,
          textAlign: "center",
          paddingBottom: "8px",
        }}
      >
        {t("confirmcanceldialog.title")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="alert-dialog-description"
          sx={{
            textAlign: "center",
            color: "#666",
            fontSize: "0.95rem",
          }}
        >
          {t("confirmcanceldialog.text")}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          padding: "16px",
          paddingTop: "8px",
          gap: "12px",
          justifyContent: "center",
        }}
      >
        <Button
          onClick={handleCancel}
          sx={{
            flex: 1,
            borderRadius: "24px",
            padding: "12px 24px",
            textTransform: "capitalize",
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: 1.2,
            backgroundColor: "#E0E0E0",
            color: "#333",
            "&:hover": {
              backgroundColor: "#D0D0D0",
            },
          }}
        >
          {t("confirmcanceldialog.cancel.label")}
        </Button>
        <Button
          onClick={handleConfirm}
          sx={{
            flex: 1,
            borderRadius: "24px",
            padding: "12px 24px",
            textTransform: "capitalize",
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: 1.2,
            backgroundColor: "#FFEBEE",
            color: "#D32F2F",
            "&:hover": {
              backgroundColor: "#FFCDD2",
            },
          }}
        >
          {t("confirmcanceldialog.confirm.label")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
